import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { Trans, useTranslation } from 'react-i18next'
import { filter, propEq } from 'ramda'
import config from '@/config'
import { H5, Label } from '@/components/text'
import Panel from '@/components/panel'
import { Stack, Box } from '@/components/structure'
import CurrencyFormatter from '@/components/currency-formatter'
import { DefaultButtonBar } from '@/components/button-bar'
import { Button } from '@/components/button'
import { breakpoints } from '@/styles'
import {
  useBank,
  useAccountType,
  formatAccountNumber,
  useReviewPadContracts,
  Contract
} from '@/features/Pad'
import useGetPadInfo from '@/hooks/use-get-pad-info'
import { isNullOrEmpty } from '@/utils'
import InlineBanner from '@/components/inline-banner'
import { RightArrowIcon } from '@/components/icons'
import isProfileVerified from '@/utils/is-verified'
import useProfile from '@/hooks/use-profile'
import { PaymentData } from '@/features/Payments/types'

const { APP_PATH } = config

type PadPaymentRequest = {
  paymentData: PaymentData
  listingId: string
  paymentId: string
}

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin: 0;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const ContentWrapper = styled.div`
  max-width: 600px;
  width: calc(100% - 40px);
  margin: 0 20px 100px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: calc(100% - 20px);
    margin: 0 10px 70px;
  }
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0 8px;
  > a {
    white-space: nowrap;
  }
`

const reload = () => {
  if (typeof window === 'undefined') return
  return window.location.reload()
}

const PadPaymentReview: React.FC<PadPaymentRequest> = ({ paymentData, listingId, paymentId }) => {
  const { t } = useTranslation()
  const reviewContracts = useReviewPadContracts() as Array<Contract>
  const connectedContracts = filter(propEq('payment_method', 'pad'), reviewContracts)
  const hasActivePad = !isNullOrEmpty(connectedContracts)
  const paidWithText = t('b.payment.view.pad_description.label', 'PAD (Pre-Authorized Debit)')

  const profile = useProfile()
  const { data: paymentMethodInfo, isLoading } = useGetPadInfo()
  const hasPadConnected = !isNullOrEmpty(paymentMethodInfo)

  const isVerified = isProfileVerified(profile)
  const isPendingVerified = profile?.identity_verified_state === 'pending'
  const canConfirm = isVerified && hasPadConnected && !hasActivePad

  const financialInfo = paymentMethodInfo?.financial_info
  const bank = useBank(financialInfo?.financial_institution_name)
  const account = useAccountType(financialInfo?.account_type)
  const accountNumber = formatAccountNumber(financialInfo?.account_number)
  return (
    <>
      <Wrapper>
        <ContentWrapper>
          {!canConfirm && !isLoading && (
            <Box top={40} display="block">
              <InlineBanner
                theme="warning"
                icon="warning_alert"
                space="fit"
                isShow={!isVerified && !isPendingVerified}
                body={
                  <Flex>
                    <div>
                      <Trans i18nKey="b.payment.view.pad_request.verify_id.banner">
                        Please verify your ID to continue. Once complete, please{' '}
                        <Button space="none" theme="text" onClick={reload}>
                          refresh this page
                        </Button>{' '}
                        and try again.
                      </Trans>
                    </div>
                    <Button
                      space="none"
                      theme="text"
                      href={`${APP_PATH}/profile`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('b.profile.edit.verify_id.text', 'Verify ID')} <RightArrowIcon />
                    </Button>
                  </Flex>
                }
              />
              <InlineBanner
                theme="warning"
                icon="warning_alert"
                space="fit"
                isShow={isPendingVerified && !isVerified}
                body={t(
                  'b.payment.view.pad_request.verify_id_pending.banner',
                  'Pending ID Verification...'
                )}
              />
              <InlineBanner
                theme="warning"
                icon="warning_alert"
                space="fit"
                isShow={!hasPadConnected && isVerified}
                body={
                  <Flex>
                    <div>
                      <Trans i18nKey="b.payment.view.pad_request.bank_connect.banner">
                        Please connect your bank acount to continue. Once complete, please{' '}
                        <Button space="none" theme="text" onClick={reload}>
                          refresh this page
                        </Button>{' '}
                        and try again.
                      </Trans>
                    </div>
                    <Button
                      space="none"
                      theme="text"
                      href={`${APP_PATH}/settings/payment-settings/pre-authorized-debit`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('b.contract.view.pad-connect.button', 'Connect')} <RightArrowIcon />
                    </Button>
                  </Flex>
                }
              />
              <InlineBanner
                theme="error"
                icon="warning_alert"
                space="fit"
                isShow={hasActivePad && hasPadConnected && isVerified}
                body={t(
                  'b.payment.view.pad_request.pad_existing.banner',
                  'Error: you can only have one active PAD agreement at a time. Please end the existing PAD agreement, then try again.'
                )}
              />
            </Box>
          )}
          <Box top={40} bottom={24}>
            <H5>{t('b.payment.view.pad_payment_requests.label', 'PAD Payment Requests')}</H5>
          </Box>
          <Panel hSpace={24} vSpace={24}>
            <Stack gutter={20}>
              <Row>
                <Label type="small">{t('b.payment.view.detail.label', 'Detail')}</Label>
                <Label>{paymentData.description}</Label>
              </Row>
              <Row>
                <Label type="small">
                  {t('b.payment.view.payment_method.label', 'Payment method')}
                </Label>
                <Label>{paidWithText}</Label>
              </Row>
              <Row>
                <Label type="small">
                  {t('b.payment.view.payment_rent_price.label', 'Rent price')}
                </Label>
                <Label>
                  <CurrencyFormatter value={paymentData.requested_amount} showCents />
                </Label>
              </Row>
              <Row>
                <Label type="small">
                  {t('b.payment.view.payment_additional_fee.label', 'Additional Fee')}
                </Label>
                <Label>
                  <CurrencyFormatter value={paymentData.additional_fee} showCents />
                </Label>
              </Row>
              <Row>
                <Label type="small">
                  {t('b.payment.view.payment_frequency.label', 'Payment frequency')}
                </Label>
                <Label>Monthly</Label>
              </Row>
              <Row>
                <Label type="small">
                  {t('b.payment.view.payment_start_date.label', 'Payment start date')}
                </Label>
                <Label>{moment(paymentData.due_date).format('MM-DD-YYYY')}</Label>
              </Row>
            </Stack>
          </Panel>
          {hasPadConnected && (
            <>
              <Box top={40} bottom={24}>
                <H5>
                  {t('b.contract.view.direct_debit_information.title', 'Direct Debit Information')}
                </H5>
              </Box>
              <Panel hSpace={24} vSpace={24}>
                <Stack gutter={20}>
                  <Row>
                    <Label type="small">
                      {t('b.contract.view.financial_institution.label', 'Financial institution')}
                    </Label>
                    <Label>{bank}</Label>
                  </Row>
                  <Row>
                    <Label type="small">
                      {t('b.contract.view.account_type.label', 'Account type')}
                    </Label>
                    <Label>{account}</Label>
                  </Row>
                  <Row>
                    <Label type="small">
                      {t('b.contract.view.account_number.label', 'Account number')}
                    </Label>
                    <Label>{accountNumber}</Label>
                  </Row>
                </Stack>
              </Panel>
            </>
          )}
        </ContentWrapper>
      </Wrapper>
      <DefaultButtonBar>
        <Button
          size="small"
          disabled={!canConfirm}
          to={`/rental/${listingId}/payments/${paymentId}/review`}
        >
          {t('b.payment_settings.edit.review_terms.text', 'Review Terms')}
        </Button>
      </DefaultButtonBar>
    </>
  )
}

export default PadPaymentReview
